import React from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatIDR } from "@/lib/format";
import { Plus, Trash2, Package as PackageIcon } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const applyDisc = (line, disc, type) => (type === "percent" ? line * (disc || 0) / 100 : (disc || 0));

export default function BookingNewPage() {
  const nav = useNavigate();
  const [customers, setCustomers] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [packages, setPackages] = React.useState([]);
  const [pkgOpen, setPkgOpen] = React.useState(false);
  const [form, setForm] = React.useState({
    customer_id: "", project_name: "", production_type: "Commercial",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10),
    prep_date: new Date().toISOString().slice(0, 10),
    delivery_method: "pickup", delivery_address: "",
    tax_rate: 11, discount: 0, discount_type: "nominal",
    delivery_fee: 0, guard_fee: 0, crew_fee: 0, other_fees: 0,
    deposit: 5000000, deposit_in_total: false, notes: "", status: "draft",
  });
  const [items, setItems] = React.useState([]);
  const [conflicts, setConflicts] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const [c, p, pk] = await Promise.all([api.get("/customers"), api.get("/products"), api.get("/packages")]);
      setCustomers(c.data); setProducts(p.data); setPackages(pk.data);
    })();
  }, []);

  const addItem = () => setItems([...items, { product_id: "", qty: 1, days: 1, unit_price: 0, discount: 0, discount_type: "nominal", asset_ids: [] }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    if (patch.product_id) {
      const p = products.find((pp) => pp.id === patch.product_id);
      if (p) next[i].unit_price = p.daily_price;
    }
    setItems(next);
  };

  const days = React.useMemo(() => {
    const s = new Date(form.start_date), e = new Date(form.end_date);
    return Math.max(1, Math.round((e - s) / 86400000) + 1);
  }, [form.start_date, form.end_date]);

  React.useEffect(() => {
    setItems((prev) => prev.map((it) => ({ ...it, days })));
  }, [days]);

  const addPackage = async (pid) => {
    try {
      const { data } = await api.get(`/packages/${pid}/expand`, { params: { days } });
      setItems([...items, ...data.items]);
      toast.success(`Paket "${data.package.name}" ditambahkan (${data.items.length} item)`);
      setPkgOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totals = React.useMemo(() => {
    let subtotal = 0, itemDisc = 0;
    items.forEach((it) => {
      const line = (it.qty || 0) * (it.days || 0) * (it.unit_price || 0);
      subtotal += line;
      itemDisc += applyDisc(line, it.discount, it.discount_type);
    });
    const afterItem = subtotal - itemDisc;
    const bookingDisc = applyDisc(afterItem, form.discount, form.discount_type);
    const fees = (form.delivery_fee || 0) + (form.guard_fee || 0) + (form.crew_fee || 0) + (form.other_fees || 0);
    const taxable = Math.max(afterItem - bookingDisc, 0) + fees;
    const tax = Math.round(taxable * (form.tax_rate || 0) / 100);
    const total = taxable + tax + (form.deposit_in_total ? (form.deposit || 0) : 0);
    const totalQty = items.reduce((s, it) => s + (it.qty || 0), 0);
    return { subtotal, itemDisc, bookingDisc, fees, taxable, tax, total, totalQty };
  }, [items, form]);

  const checkAvailability = async () => {
    try {
      const { data } = await api.post("/availability/check", {
        start_date: form.start_date, end_date: form.end_date, items,
      });
      setConflicts(data.conflicts);
      if (data.ok) toast.success("Semua equipment tersedia");
      else toast.warning(`${data.conflicts.length} konflik ditemukan`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.customer_id) return toast.error("Pilih pelanggan");
    if (items.length === 0) return toast.error("Tambahkan minimal 1 equipment");
    setBusy(true);
    try {
      const body = { ...form, items: items.map((it) => ({ ...it, days })) };
      const { data } = await api.post("/bookings", body);
      toast.success("Booking dibuat: " + data.number);
      nav(`/bookings/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="booking-new-page">
      <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Booking Baru</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-flat p-4 lg:col-span-2 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Pelanggan</Label>
              <select
                className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white"
                value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                data-testid="booking-customer"
              >
                <option value="">— Pilih pelanggan —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Nama Project</Label>
              <Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} data-testid="booking-project" />
            </div>
            <div>
              <Label>Tanggal Mulai</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="booking-start" />
            </div>
            <div>
              <Label>Tanggal Selesai</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="booking-end" />
            </div>
            <div>
              <Label>Durasi Sewa</Label>
              <div className="h-9 flex items-center px-3 border border-neutral-200 rounded-md bg-green-50 text-sm font-semibold text-green-800" data-testid="booking-duration">
                {days} hari
              </div>
            </div>
            <div>
              <Label>Metode Ambil</Label>
              <select
                className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white"
                value={form.delivery_method}
                onChange={(e) => setForm({ ...form, delivery_method: e.target.value })}
                data-testid="booking-delivery"
              >
                <option value="pickup">Ambil Sendiri</option>
                <option value="delivery">Kirim</option>
              </select>
            </div>
          </div>

          <div className="border-t border-neutral-200 pt-3">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-sm font-semibold">
                Equipment <span className="text-xs text-neutral-500 font-normal ml-1">
                  ({items.length} baris · Total {totals.totalQty} unit · {days} hari)
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="add-package-btn"><PackageIcon className="h-4 w-4 mr-1" /> Tambah Paket</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Pilih Paket Equipment</DialogTitle></DialogHeader>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {packages.length === 0 && <div className="text-sm text-neutral-500 text-center py-4">Belum ada paket. Buat dulu di menu Paket Equipment.</div>}
                      {packages.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => addPackage(p.id)}
                          className="w-full text-left p-3 border border-neutral-200 rounded hover:border-green-600 hover:bg-green-50 transition-colors"
                          data-testid={`pkg-option-${p.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-sm">{p.name}</div>
                              <div className="text-xs text-neutral-500">{p.code} · {p.items?.length || 0} komponen</div>
                            </div>
                            <div className="text-sm font-semibold text-green-700">{formatIDR(p.daily_price)}/hari</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" size="sm" onClick={checkAvailability} data-testid="check-availability">Cek Ketersediaan</Button>
                <Button size="sm" onClick={addItem} className="bg-green-600 hover:bg-green-700" data-testid="add-item">
                  <Plus className="h-4 w-4 mr-1" /> Tambah Item
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th className="text-left">Equipment</th>
                    <th className="w-16 text-right">Qty</th>
                    <th className="w-16 text-right">Hari</th>
                    <th className="w-32 text-right">Harga/hari</th>
                    <th className="w-40 text-right">Diskon</th>
                    <th className="w-32 text-right">Subtotal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const line = (it.qty || 0) * (it.days || 0) * (it.unit_price || 0);
                    const disc = applyDisc(line, it.discount, it.discount_type);
                    return (
                      <tr key={i}>
                        <td>
                          {it.package_name && <div className="text-[10px] text-green-700 mb-0.5" data-testid="item-package-label">📦 Paket: {it.package_name}</div>}
                          <select className="w-full border border-neutral-200 rounded px-2 py-1 text-sm"
                            value={it.product_id} onChange={(e) => updateItem(i, { product_id: e.target.value })}
                            data-testid={`item-product-${i}`}>
                            <option value="">— pilih —</option>
                            {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.ownership === "vendor" ? ` (${p.vendor_name || "Vendor"})` : ""}</option>)}
                          </select>
                        </td>
                        <td><Input type="number" min={1} value={it.qty} onChange={(e) => updateItem(i, { qty: +e.target.value })} className="text-right" data-testid={`item-qty-${i}`} /></td>
                        <td><Input type="number" min={1} value={it.days} onChange={(e) => updateItem(i, { days: +e.target.value })} className="text-right" data-testid={`item-days-${i}`} /></td>
                        <td><Input type="number" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: +e.target.value })} className="text-right" /></td>
                        <td>
                          <div className="flex gap-1">
                            <Input type="number" value={it.discount} onChange={(e) => updateItem(i, { discount: +e.target.value })} className="text-right" data-testid={`item-discount-${i}`} />
                            <select value={it.discount_type || "nominal"} onChange={(e) => updateItem(i, { discount_type: e.target.value })} className="border border-neutral-200 rounded px-1 text-xs bg-white" data-testid={`item-discount-type-${i}`}>
                              <option value="nominal">Rp</option>
                              <option value="percent">%</option>
                            </select>
                          </div>
                        </td>
                        <td className="text-right font-medium">{formatIDR(line - disc)}</td>
                        <td><button onClick={() => removeItem(i)} className="text-red-500" data-testid={`remove-item-${i}`}><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-4 text-sm text-neutral-500">Belum ada equipment. Tambahkan item atau pilih paket.</td></tr>
                  )}
                  {items.length > 0 && (
                    <tr className="bg-neutral-50 font-medium">
                      <td className="text-right text-xs uppercase text-neutral-500">Ringkasan</td>
                      <td className="text-right" data-testid="items-total-qty">{totals.totalQty}</td>
                      <td className="text-right">{days}</td>
                      <td colSpan={2} className="text-right text-xs text-neutral-500">Subtotal sebelum diskon</td>
                      <td className="text-right text-green-700" data-testid="items-subtotal">{formatIDR(totals.subtotal)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {conflicts && conflicts.length > 0 && (
              <div className="mt-3 p-3 rounded border border-red-200 bg-red-50 text-sm text-red-700">
                <div className="font-medium mb-1">Konflik ketersediaan:</div>
                <ul className="list-disc pl-5">
                  {conflicts.map((c, i) => (
                    <li key={i}>{c.product_name || c.product_id}: butuh {c.needed}, tersedia {c.available}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <Label>Catatan</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="booking-notes" />
          </div>
        </div>

        <div className="card-flat p-4 space-y-3 h-fit lg:sticky lg:top-16">
          <div className="text-sm font-semibold">Ringkasan Biaya</div>

          <div>
            <Label>Diskon Booking</Label>
            <div className="flex gap-1">
              <Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: +e.target.value })} data-testid="booking-discount" />
              <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="border border-neutral-200 rounded px-2 text-xs bg-white" data-testid="booking-discount-type">
                <option value="nominal">Rp</option>
                <option value="percent">%</option>
              </select>
            </div>
          </div>

          <div>
            <Label>PPN (%)</Label>
            <Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: +e.target.value })} data-testid="booking-tax" />
          </div>

          <div className="border-t border-neutral-200 pt-2">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Biaya Tambahan</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Ongkos Kirim</Label><Input type="number" value={form.delivery_fee} onChange={(e) => setForm({ ...form, delivery_fee: +e.target.value })} data-testid="fee-delivery" /></div>
              <div><Label className="text-xs">Pengawalan / Guard</Label><Input type="number" value={form.guard_fee} onChange={(e) => setForm({ ...form, guard_fee: +e.target.value })} data-testid="fee-guard" /></div>
              <div><Label className="text-xs">Crew / Operator</Label><Input type="number" value={form.crew_fee} onChange={(e) => setForm({ ...form, crew_fee: +e.target.value })} data-testid="fee-crew" /></div>
              <div><Label className="text-xs">Lainnya</Label><Input type="number" value={form.other_fees} onChange={(e) => setForm({ ...form, other_fees: +e.target.value })} data-testid="fee-other" /></div>
            </div>
          </div>

          <div className="border-t border-neutral-200 pt-2 grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Deposit</Label><Input type="number" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: +e.target.value })} /></div>
            <div className="flex items-end gap-2"><input type="checkbox" checked={form.deposit_in_total} onChange={(e) => setForm({ ...form, deposit_in_total: e.target.checked })} /> <Label className="text-xs">Deposit di total</Label></div>
          </div>

          <div className="border-t border-neutral-200 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-neutral-500">Subtotal ({totals.totalQty} unit × {days} hari)</span><span data-testid="sum-subtotal">{formatIDR(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Diskon Item</span><span>-{formatIDR(totals.itemDisc)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Diskon Booking</span><span data-testid="sum-booking-disc">-{formatIDR(totals.bookingDisc)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Biaya Tambahan</span><span>{formatIDR(totals.fees)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">PPN {form.tax_rate}%</span><span>{formatIDR(totals.tax)}</span></div>
            <div className="flex justify-between font-bold text-base border-t border-neutral-200 pt-2">
              <span>Grand Total</span><span className="text-green-700" data-testid="sum-total">{formatIDR(totals.total)}</span>
            </div>
          </div>
          <Button onClick={save} disabled={busy} className="w-full bg-green-600 hover:bg-green-700" data-testid="save-booking">
            {busy ? "Menyimpan…" : "Simpan Booking"}
          </Button>
        </div>
      </div>
    </div>
  );
}
